/**
 * Cloud Agent — scope comparison. Pure, React-free (testable without a DOM).
 *
 * Scope is immutable for the life of a conversation: changing it discards the transcript and
 * starts a new one. So "is this the same scope?" has to be a SET comparison, not a reference or
 * array comparison — re-applying the same restaurants in a different order, or re-checking a
 * box you just unchecked, must not cost the super-admin their conversation.
 *
 * Mirrors the server's `scopesMatch` in `domains/cloudAgent/conversationStore.ts`, which is the
 * authority: a mismatch there is a 400 `scope_mismatch`. This copy only decides whether the
 * frontend bothers to reset first.
 */
import type { CloudAgentScope } from './cloudAgentTypes';

/** Every restaurant id a scope names. Empty for `all` (which names them implicitly). */
export function scopeRestaurantIds(scope: CloudAgentScope): string[] {
  if (scope.mode === 'restaurant') return [scope.restaurantId];
  if (scope.mode === 'selected') return scope.restaurantIds;
  return [];
}

/**
 * What the picker's checkbox set should start from when the dropdown opens.
 *
 * ONLY a `selected` scope pre-ticks. A single-restaurant scope must not, and that is a bug fix
 * rather than a preference: seeding it left the current restaurant ticked while the admin
 * switched to a different client, where its checkbox was off-screen and its client was no longer
 * the one being browsed — so ticking 4 restaurants of client B silently applied a 5-restaurant
 * scope spanning two clients. `all` names its restaurants implicitly and has no ids to seed.
 */
export function seedSelection(scope: CloudAgentScope | null): string[] {
  if (!scope || scope.mode !== 'selected') return [];
  return Array.from(new Set(scope.restaurantIds));
}

/**
 * Order-insensitive, duplicate-insensitive set equality. Iterates the ARRAY rather than the
 * Set — this tsconfig's target predates downlevel Set iteration.
 */
function sameIdSet(a: string[], b: string[]): boolean {
  const right = new Set(b);
  if (new Set(a).size !== right.size) return false;
  return a.every((id) => right.has(id));
}

/**
 * Do these two scopes cover exactly the same data? `userId` is deliberately ignored — it is a
 * resolution hint for the server, not part of what the scope covers.
 */
export function scopesMatch(a: CloudAgentScope | null, b: CloudAgentScope | null): boolean {
  if (!a || !b) return false;
  if (a.mode !== b.mode) return false;
  if (a.mode === 'all') return true;
  return sameIdSet(scopeRestaurantIds(a), scopeRestaurantIds(b));
}

/**
 * Build the scope for a set of checked restaurant ids:
 *  - none          → null (not a scope; the picker keeps Apply disabled)
 *  - exactly one   → `restaurant` (the cheaper server path, and the only mode carrying `userId`)
 *  - all of them   → `all` (see below)
 *  - otherwise     → `selected`
 *
 * The `all` collapse keeps the wire canonical: an explicit subset naming every restaurant covers
 * exactly what `all` covers, but `all` is the cheaper way for the server to express that same
 * query set. It applies only from two restaurants up — a lone restaurant stays `restaurant` mode
 * even when it is the only one that exists, because that mode is more specific, carries `userId`,
 * and does not expose the cross-restaurant computations.
 *
 * `allRestaurantsTotal` must be the size of the FULL universe, not just what the caller is
 * showing — pass 0 (the default) when that is unknown, and no collapse happens. Getting this
 * wrong would silently WIDEN the scope, so the caller only passes a total it has verified.
 */
export function scopeForSelection(
  restaurantIds: string[],
  resolveUserId: (restaurantId: string) => string | undefined,
  allRestaurantsTotal = 0
): CloudAgentScope | null {
  const uniqueIds = Array.from(new Set(restaurantIds));
  if (uniqueIds.length === 0) return null;
  if (uniqueIds.length === 1) {
    const restaurantId = uniqueIds[0];
    const userId = resolveUserId(restaurantId);
    return { mode: 'restaurant', restaurantId, ...(userId ? { userId } : {}) };
  }
  if (allRestaurantsTotal > 0 && uniqueIds.length === allRestaurantsTotal) return { mode: 'all' };
  return { mode: 'selected', restaurantIds };
}
